/**
 * Submenu trigger component.
 *
 * Can be used with simple props (icon + label) or custom children.
 * The simple API encapsulates styling so consumers don't need to import styles.
 */

import type { ReactNode } from "react";
import * as stylex from "@stylexjs/stylex";
import * as BloomMenu from "bloom-menu";

import { Icon } from "../icon";
import { styles } from "./menu-styles.stylex";

export interface MenuSubMenuTriggerProps {
  /** Custom render function or element. If using icon+label, omit this. */
  children?: ReactNode | ((isActive: boolean) => ReactNode);
  /** Icon displayed on the left (simple API) */
  icon?: ReactNode;
  /** Label text (simple API) */
  label?: string;
}

const chevronSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;

export function MenuSubMenuTrigger({
  children,
  icon,
  label,
}: MenuSubMenuTriggerProps) {
  // Simple API: icon + label with built-in styling
  if (icon !== undefined && label !== undefined) {
    return (
      <div {...stylex.props(styles.subMenuTrigger)}>
        <BloomMenu.SubMenuTrigger>
          {() => (
            <div {...stylex.props(styles.subMenuTriggerContent)}>
              {icon}
              <span>{label}</span>
              <Icon size="xs" svg={chevronSvg} xstyle={styles.chevron} />
            </div>
          )}
        </BloomMenu.SubMenuTrigger>
      </div>
    );
  }

  // Advanced API: custom children
  return (
    <div {...stylex.props(styles.subMenuTrigger)}>
      <BloomMenu.SubMenuTrigger>{children}</BloomMenu.SubMenuTrigger>
    </div>
  );
}
